import { useListPager } from '@airship-platform/airship-patterns';
import { FlexBox, FlexBoxProps } from '@airship-platform/jam-web/components';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { Fragment } from 'react/jsx-runtime';
import { AppointmentHistory } from '../../../../data-access/appointmentHistory';
import ROUTE_PATHS from '../../../../routes/paths';
import ScheduleCard from '../ScheduleCard';
import ScheduleCardAction from '../ScheduleCardAction';
import AppointmentSearchListChips from './AppointmentSearchListChips';
import AppointmentSearchListContent from './AppointmentSearchListContent';

type AppointmentSearchProps = FlexBoxProps & {
  appointments: AppointmentHistory[];
  pageSize: number;
  onPageSizeChange: (newFilter: Partial<{ pageSize: number }>) => void;
};

const getCardActionProps = (appointment: AppointmentHistory) => {
  const proposal = appointment.proposals.at(0);

  const warrantyAction = {
    label: 'Carrier warranty',
    href: `/proposal-warranties/appointments/${appointment.appointmentId}`,
    visible: appointment.status === 'COMPLETED',
  };

  if (!proposal) {
    return {
      color: 'softBlue' as const,
      children: 'Build Proposal',
      quickActions: [],
    };
  }

  return {
    color: 'softGold' as const,
    children: 'Follow up',
    to: ROUTE_PATHS.APPOINTMENT_PATH({
      appointmentId: appointment.appointmentId,
      appointmentProposalId: proposal.appointmentProposalId,
      path: 'follow-up',
    }),
    quickActions: [
      {
        label: 'View proposal',
        to: ROUTE_PATHS.APPOINTMENT_PATH({
          appointmentId: appointment.appointmentId,
          appointmentProposalId: proposal.appointmentProposalId,
          path: 'present',
        }),
      },
      {
        label: 'View options',
        to: ROUTE_PATHS.APPOINTMENT_PATH({
          appointmentId: appointment.appointmentId,
          appointmentProposalId: proposal.appointmentProposalId,
          path: 'options',
        }),
      },
      {
        label: 'View job details',
        to: ROUTE_PATHS.SCHEDULE_DETAIL(appointment.appointmentId),
      },
      {
        label: 'View activities',
        to: ROUTE_PATHS.APPOINTMENT_PATH({
          appointmentId: appointment.appointmentId,
          appointmentProposalId: proposal.appointmentProposalId,
          path: 'activity',
        }),
      },
      warrantyAction,
    ],
  };
};

const AppointmentSearchList: React.FC<AppointmentSearchProps> = ({
  appointments,
  pageSize,
  onPageSizeChange,
  ...props
}) => {
  const { loaderRef } = useListPager({
    incrementBy: 20,
    initialValue: pageSize,
    maxValue: appointments.length,
    callbackFn: (newPageSize) => onPageSizeChange({ pageSize: newPageSize }),
  });

  const pagedAppointments = appointments.slice(0, pageSize);

  return (
    <FlexBox {...props}>
      {pagedAppointments.map((appointment) => (
        <Fragment key={appointment.appointmentId}>
          <ScheduleCard
            appointment={{
              startDateTime: appointment.startDateTime,
            }}
            customer={{
              name: appointment.customerName,
              address: appointment.customerAddress.address1,
              city: appointment.customerAddress.city,
              state: appointment.customerAddress.state,
              zip: appointment.customerAddress.zip,
            }}
            subHeaderContent={
              <AppointmentSearchListChips appointment={appointment} />
            }
            cardAction={
              <FlexBox flexDirection={'column'} gap={1} alignItems={'center'}>
                <ScheduleCardAction
                  appointmentId={appointment.appointmentId}
                  {...getCardActionProps(appointment)}
                />
              </FlexBox>
            }
          >
            <AppointmentSearchListContent
              appointment={appointment}
              flexDirection={'column'}
              gap={0.5}
            />
          </ScheduleCard>
          <Divider flexItem />
        </Fragment>
      ))}
      <Box ref={loaderRef} sx={{ height: 50 }} />
    </FlexBox>
  );
};

export default AppointmentSearchList;
